/**
 * `shellDensity` — the app's viewport breakpoints and row-density scale, in one
 * place.
 *
 * Shell: any. This module is pure data — no React, no store, no Supabase — so
 * it is safe in `(kiosk)`, `(desktop)` and `(mobile)` alike.
 *
 * ## Why this file exists
 *
 * Before it, the same two numbers and the same three paddings were written out
 * in three unrelated places and nothing kept them together:
 *
 * | Where | What it hard-coded |
 * | --- | --- |
 * | `hooks/useShellBreakpoint.ts` | `(max-width: 1279px)` / `(min-width: 1601px)` |
 * | `shells/desktop/DesktopLayout.module.css` | the same edges again, as three `@media` blocks |
 * | `components/ds/DataTable.tsx` | `{ compact: 4, default: 7, comfortable: 10 }` |
 *
 * They are one system — the table's density *is* derived from the viewport
 * tier — so they now read from here. See `mtb-design-system/platforms/alignment.md`
 * §3 and issue #107.
 *
 * ## PENDING UPSTREAM — these values belong in the design system
 *
 * They are **not** design-system tokens yet, and this module is the interim
 * single source until they are. `mtb-design-system/tokens/density.css` carries
 * control and row *heights* (`--row-h`, `--control-h-*`) on a two-tier
 * `comfortable`/`compact` axis; it has no cell *padding* tier and no
 * breakpoints at all. Four frontends have to agree on a breakpoint, so a
 * breakpoint that lives in one app's component is a breakpoint nobody else can
 * honour.
 *
 * When the design system ships `--row-pad-y-compact/default/comfortable` and
 * `--breakpoint-shell-compact/wide`, **delete the literals below and read the
 * tokens** — the exported shapes should not need to change.
 *
 * ## Vocabulary — three names, and what they map onto
 *
 * The app uses three density names; two other vocabularies are adjacent and
 * neither is a synonym, so the mapping is spelled out rather than assumed:
 *
 * | This module | `useUi().tableDensity` | `<html data-density>` (design system) |
 * | --- | --- | --- |
 * | `compact` | `compact` | `compact` |
 * | `default` | `comfortable` (persists as) | `compact` |
 * | `comfortable` | `comfortable` | `compact` |
 *
 * `<html data-density>` is fixed at `compact` for this app (see `index.html`):
 * it drives the design system's *control* heights, which are a property of the
 * product (desk tooling, not touch-first), not of the viewport. Row density is
 * the viewport-and-preference axis and is what this module governs.
 */

/** The three row-density tiers, coarsest to loosest. */
export type RowDensity = 'compact' | 'default' | 'comfortable';

/** The three desktop-shell viewport tiers. */
export type ShellBreakpoint = 'compact' | 'default' | 'wide';

/**
 * Viewport edges, in CSS pixels.
 *
 * `compactMax` is the last width that still counts as compact; `wideMin` is the
 * first that counts as wide. Everything between is the `default` tier — the
 * design target, a typical staff workstation.
 */
export const SHELL_BREAKPOINTS = {
  /** 13" laptops and shared screens sit at or below this. */
  compactMax: 1279,
  /** Large external monitors and staff-station kiosks sit at or above this. */
  wideMin: 1601,
} as const;

/**
 * The media queries derived from {@link SHELL_BREAKPOINTS}. Exported so the
 * hook and its tests agree on one string apiece rather than two literals that
 * happen to match.
 */
export const SHELL_MEDIA_QUERIES = {
  compact: `(max-width: ${SHELL_BREAKPOINTS.compactMax}px)`,
  wide: `(min-width: ${SHELL_BREAKPOINTS.wideMin}px)`,
} as const;

/**
 * Vertical cell padding per row-density tier, in CSS pixels.
 *
 * These are the values the app has always shipped; they are moved, not
 * retuned. Retuning them is a design-system decision, not a refactor.
 */
export const ROW_PAD_Y: Record<RowDensity, number> = {
  compact: 4,
  default: 7,
  comfortable: 10,
};

/** Mantine font-size token per row-density tier. */
export const ROW_FONT_SIZE: Record<RowDensity, 'xs' | 'sm'> = {
  compact: 'xs',
  default: 'sm',
  comfortable: 'sm',
};

/**
 * Maps a viewport tier onto the row density it implies.
 *
 * A compact viewport tightens rows; a wide one loosens them. The `default`
 * tier deliberately returns `null` — there the user's persisted preference
 * wins, and returning a tier here would silently override it.
 */
export function rowDensityForBreakpoint(breakpoint: ShellBreakpoint): RowDensity | null {
  if (breakpoint === 'compact') return 'compact';
  if (breakpoint === 'wide') return 'comfortable';
  return null;
}
